import React, { useEffect, useMemo, useState } from "react";
import EmailTemplateEdit from "./EmailTemplateEdit";
import EmailTemplateAdd from "./EmailTemplateAdd";
import api from "../../../config/URL";
import { Link } from "react-router-dom";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { exportVisibleColumnsToCSV } from "../../../components/common/ExportCsv";

const EmailTemplate = () => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");

  const canIndex = Boolean(storedScreens?.emailTemplateIndex);
  const canRead = Boolean(storedScreens?.emailTemplateRead);
  const canCreate = Boolean(storedScreens?.emailTemplateCreate);
  const canUpdate = Boolean(storedScreens?.emailTemplateUpdate);
  const showActionColumn = canUpdate;

  const getData = async () => {
    try {
      const response = await api.get("/getAllEmailTemplates");
      setDatas(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "subject",
        header: "Subject",
        enableHiding: true,
      },
      // {
      //   accessorKey: "description",
      //   enableHiding: false,
      //   header: "Description",
      //   size: 20,
      //   Cell: ({ cell }) => {
      //     const content = cell.getValue(); // Assuming cell.getValue() fetches the raw description HTML.
      //     return (
      //       <div
      //         dangerouslySetInnerHTML={{
      //           __html: content || "<p>No Description Available</p>",
      //         }}
      //         style={{
      //           maxHeight: "100px", // Add styling to limit the height, if necessary.
      //           overflow: "hidden", // Ensure overflow handling.
      //           textOverflow: "ellipsis",
      //         }}
      //       />
      //     );
      //   },
      // },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    return [baseColumns[0], actionColumn, ...baseColumns.slice(1)];
  }, [showActionColumn]);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data: datas,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, datas, "EmailTemplate.csv")
        }
      >
        Export CSV
      </button>
    ),
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3 px-1"
        style={{ listStyle: "none", margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Settings<span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">&nbsp;Email Template</li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="dot active"></div>
            <span className="ms-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Email Template
              </span>
            </span>
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-center">
          {/* {canCreate && ( */}
          {/* <span>
            <EmailTemplateAdd onSuccess={refreshData} />
          </span> */}
          {/* )}*/}
          {/* } */}
          {/* <p>        <button className="btn btn-light border-secondary mx-2" onClick={handleDataShow}>

          {extraData?"Hide":'Show'}
          <MdViewColumn className="fs-4 text-secondary"/>

        </button> </p> */}
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {canUpdate && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem>
                  <EmailTemplateEdit
                    id={selectedId}
                    onSuccess={getData}
                    handleMenuClose={handleMenuClose}
                  />
                </MenuItem>
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmailTemplate;
